import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Form, Input, message } from 'antd';
import {
  CheckCircleOutlined,
  GiftOutlined,
  LinkOutlined,
  LockOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SendOutlined,
  TeamOutlined,
  UserAddOutlined,
  UserOutlined
} from '@ant-design/icons';
import AuthShell from './AuthShell';
import { publicAPI } from '../services/api';
import { getGameLanguageId, normalizeUiLanguage, REGISTER_LANGUAGE_OPTIONS } from '../i18n';

const REGISTER_COPY_EN = {
  captchaLoadError: 'Unable to load the security code. Refresh and try again.',
  success: 'Account created. Return to sign in with your new account.',
  error: 'Registration failed. Check the information and try again.',
  brandName: 'Account Invite',
  brandSubtitle: 'Player Registration',
  eyebrow: 'Invite Registration',
  title: 'Create Player Account',
  description: 'Create an account with an invitation code and security check.',
  statType: 'Type',
  statRegister: 'Register',
  statInvite: 'Invite Code',
  recognized: 'Recognized',
  missing: 'Missing',
  statCheck: 'Check',
  captcha: 'Security Code',
  featureTitle: 'Registration',
  featureInvite: 'Invitation Link',
  featureInviteText: 'The invitation code is read from this link.',
  featureAccount: 'Create Account',
  featureAccountText: 'Only the required account information is collected.',
  featureRelation: 'Invitation Relation',
  featureRelationText: 'The invitation relation is retained after registration.',
  featureValidation: 'Validation',
  featureValidationText: 'Password confirmation and the security code are checked before submission.',
  panelTitle: 'Create Player Account',
  panelDescription: 'Enter account information to complete registration.',
  inviteCurrent: 'Invitation code: ',
  inviteMissing: 'This link does not contain an invitation code.',
  footer: 'Already have an account? Return to sign in',
  username: 'Account Name',
  usernameRequired: 'Enter an account name.',
  usernameMin: 'The account name must contain at least 6 characters.',
  usernameMax: 'The account name cannot exceed 20 characters.',
  usernamePlaceholder: 'Use 6-20 letters or numbers',
  password: 'Password',
  passwordRequired: 'Enter a password.',
  passwordMin: 'The password must contain at least 6 characters.',
  passwordPlaceholder: 'At least 6 characters',
  confirmPassword: 'Confirm Password',
  confirmRequired: 'Confirm the password.',
  confirmMismatch: 'The passwords do not match.',
  confirmPlaceholder: 'Enter the password again',
  captchaRequired: 'Enter the security code.',
  captchaLength: 'The security code contains 4 characters.',
  captchaPlaceholder: '4-character code',
  refreshCaptcha: 'Refresh security code',
  submit: 'Create Account',
  lightMode: 'Light mode',
  darkMode: 'Dark mode',
  language: 'Language'
};

const REGISTER_COPY_ZH = {
  captchaLoadError: '验证码加载失败，请刷新后重试',
  success: '注册成功，请返回登录入口使用新账号',
  error: '注册失败，请检查信息后重试',
  brandName: '邀请注册',
  brandSubtitle: '玩家账号注册',
  eyebrow: '邀请注册',
  title: '创建玩家账号',
  description: '通过邀请码和安全校验创建玩家账号。',
  statType: '入口类型',
  statRegister: '注册',
  statInvite: '邀请码',
  recognized: '已识别',
  missing: '未携带',
  statCheck: '校验',
  captcha: '安全验证码',
  featureTitle: '注册流程',
  featureInvite: '邀请码直达',
  featureInviteText: '链接自动携带邀请码，无需重复复制',
  featureAccount: '快速建号',
  featureAccountText: '只填写必要信息即可完成账号创建',
  featureRelation: '推广绑定',
  featureRelationText: '注册后保留邀请关系和后续奖励链路',
  featureValidation: '表单校验',
  featureValidationText: '密码确认和验证码会在提交前检查',
  panelTitle: '创建玩家账号',
  panelDescription: '填写账号信息，完成邀请注册。',
  inviteCurrent: '当前邀请码：',
  inviteMissing: '当前链接未携带邀请码。',
  footer: '已有账号，返回登录',
  username: '新账号名称',
  usernameRequired: '请输入用户名',
  usernameMin: '用户名至少 6 个字符',
  usernameMax: '用户名最多 20 个字符',
  usernamePlaceholder: '设置 6-20 位字母或数字',
  password: '设置密码',
  passwordRequired: '请输入密码',
  passwordMin: '密码至少 6 个字符',
  passwordPlaceholder: '至少 6 位字符',
  confirmPassword: '确认密码',
  confirmRequired: '请确认密码',
  confirmMismatch: '两次输入的密码不一致',
  confirmPlaceholder: '再次输入密码',
  captchaRequired: '请输入验证码',
  captchaLength: '验证码为 4 位字符',
  captchaPlaceholder: '4 位验证码',
  refreshCaptcha: '刷新验证码',
  submit: '提交注册',
  lightMode: '浅色模式',
  darkMode: '暗夜模式',
  language: '语言'
};

const REGISTER_COPY_RU = {
  captchaLoadError: 'Не удалось загрузить код безопасности. Обновите его и попробуйте снова.',
  success: 'Аккаунт создан. Теперь вы можете войти с новым аккаунтом.',
  error: 'Не удалось зарегистрироваться. Проверьте данные и попробуйте снова.',
  brandName: 'Регистрация по приглашению',
  brandSubtitle: 'Регистрация игрока',
  eyebrow: 'Приглашение',
  title: 'Создать аккаунт игрока',
  description: 'Создайте аккаунт с помощью кода приглашения и проверки безопасности.',
  statType: 'Тип',
  statRegister: 'Регистрация',
  statInvite: 'Код приглашения',
  recognized: 'Распознан',
  missing: 'Отсутствует',
  statCheck: 'Проверка',
  captcha: 'Код безопасности',
  featureTitle: 'Процесс регистрации',
  featureInvite: 'Ссылка-приглашение',
  featureInviteText: 'Код приглашения автоматически считывается из ссылки.',
  featureAccount: 'Создание аккаунта',
  featureAccountText: 'Требуется только основная информация об аккаунте.',
  featureRelation: 'Связь с пригласившим',
  featureRelationText: 'После регистрации связь с пригласившим сохраняется.',
  featureValidation: 'Проверка данных',
  featureValidationText: 'Перед отправкой проверяются пароль и код безопасности.',
  panelTitle: 'Создать аккаунт игрока',
  panelDescription: 'Введите данные аккаунта, чтобы завершить регистрацию.',
  inviteCurrent: 'Код приглашения: ',
  inviteMissing: 'В этой ссылке нет кода приглашения.',
  footer: 'Уже есть аккаунт? Вернуться ко входу',
  username: 'Имя аккаунта',
  usernameRequired: 'Введите имя аккаунта.',
  usernameMin: 'Имя аккаунта должно содержать не менее 6 символов.',
  usernameMax: 'Имя аккаунта не должно превышать 20 символов.',
  usernamePlaceholder: '6–20 букв или цифр',
  password: 'Пароль',
  passwordRequired: 'Введите пароль.',
  passwordMin: 'Пароль должен содержать не менее 6 символов.',
  passwordPlaceholder: 'Не менее 6 символов',
  confirmPassword: 'Подтвердите пароль',
  confirmRequired: 'Подтвердите пароль.',
  confirmMismatch: 'Пароли не совпадают.',
  confirmPlaceholder: 'Введите пароль ещё раз',
  captchaRequired: 'Введите код безопасности.',
  captchaLength: 'Код безопасности должен содержать 4 символа.',
  captchaPlaceholder: 'Код из 4 символов',
  refreshCaptcha: 'Обновить код безопасности',
  submit: 'Создать аккаунт',
  lightMode: 'Светлая тема',
  darkMode: 'Тёмная тема',
  language: 'Язык'
};

const REGISTER_COPY_VI = {
  captchaLoadError: 'Không thể tải mã bảo mật. Vui lòng làm mới và thử lại.',
  success: 'Đã tạo tài khoản. Bạn có thể đăng nhập bằng tài khoản mới.',
  error: 'Đăng ký thất bại. Vui lòng kiểm tra thông tin và thử lại.',
  brandName: 'Đăng ký qua lời mời',
  brandSubtitle: 'Đăng ký tài khoản người chơi',
  eyebrow: 'Đăng ký qua lời mời',
  title: 'Tạo tài khoản người chơi',
  description: 'Tạo tài khoản bằng mã mời và bước xác minh bảo mật.',
  statType: 'Loại',
  statRegister: 'Đăng ký',
  statInvite: 'Mã mời',
  recognized: 'Đã nhận diện',
  missing: 'Không có',
  statCheck: 'Xác minh',
  captcha: 'Mã bảo mật',
  featureTitle: 'Quy trình đăng ký',
  featureInvite: 'Liên kết mời',
  featureInviteText: 'Mã mời được tự động lấy từ liên kết này.',
  featureAccount: 'Tạo tài khoản',
  featureAccountText: 'Chỉ thu thập các thông tin tài khoản cần thiết.',
  featureRelation: 'Quan hệ giới thiệu',
  featureRelationText: 'Quan hệ giới thiệu được giữ lại sau khi đăng ký.',
  featureValidation: 'Kiểm tra dữ liệu',
  featureValidationText: 'Mật khẩu và mã bảo mật được kiểm tra trước khi gửi.',
  panelTitle: 'Tạo tài khoản người chơi',
  panelDescription: 'Nhập thông tin tài khoản để hoàn tất đăng ký.',
  inviteCurrent: 'Mã mời: ',
  inviteMissing: 'Liên kết này không có mã mời.',
  footer: 'Đã có tài khoản? Quay lại đăng nhập',
  username: 'Tên tài khoản',
  usernameRequired: 'Vui lòng nhập tên tài khoản.',
  usernameMin: 'Tên tài khoản phải có ít nhất 6 ký tự.',
  usernameMax: 'Tên tài khoản không được vượt quá 20 ký tự.',
  usernamePlaceholder: 'Dùng 6–20 chữ cái hoặc chữ số',
  password: 'Mật khẩu',
  passwordRequired: 'Vui lòng nhập mật khẩu.',
  passwordMin: 'Mật khẩu phải có ít nhất 6 ký tự.',
  passwordPlaceholder: 'Ít nhất 6 ký tự',
  confirmPassword: 'Xác nhận mật khẩu',
  confirmRequired: 'Vui lòng xác nhận mật khẩu.',
  confirmMismatch: 'Mật khẩu không khớp.',
  confirmPlaceholder: 'Nhập lại mật khẩu',
  captchaRequired: 'Vui lòng nhập mã bảo mật.',
  captchaLength: 'Mã bảo mật gồm 4 ký tự.',
  captchaPlaceholder: 'Mã gồm 4 ký tự',
  refreshCaptcha: 'Làm mới mã bảo mật',
  submit: 'Tạo tài khoản',
  lightMode: 'Chế độ sáng',
  darkMode: 'Chế độ tối',
  language: 'Ngôn ngữ'
};

const REGISTER_COPY_AR = {
  captchaLoadError: 'تعذر تحميل رمز الأمان. يرجى تحديثه والمحاولة مرة أخرى.',
  success: 'تم إنشاء الحساب. يمكنك الآن تسجيل الدخول بالحساب الجديد.',
  error: 'فشل التسجيل. يرجى التحقق من المعلومات والمحاولة مرة أخرى.',
  brandName: 'التسجيل عبر دعوة',
  brandSubtitle: 'تسجيل حساب اللاعب',
  eyebrow: 'تسجيل بدعوة',
  title: 'إنشاء حساب لاعب',
  description: 'أنشئ حسابًا باستخدام رمز الدعوة والتحقق الأمني.',
  statType: 'النوع',
  statRegister: 'تسجيل',
  statInvite: 'رمز الدعوة',
  recognized: 'تم التعرّف عليه',
  missing: 'غير موجود',
  statCheck: 'التحقق',
  captcha: 'رمز الأمان',
  featureTitle: 'خطوات التسجيل',
  featureInvite: 'رابط الدعوة',
  featureInviteText: 'يتم قراءة رمز الدعوة تلقائيًا من هذا الرابط.',
  featureAccount: 'إنشاء الحساب',
  featureAccountText: 'يتم طلب معلومات الحساب الضرورية فقط.',
  featureRelation: 'علاقة الدعوة',
  featureRelationText: 'يتم الاحتفاظ بعلاقة الدعوة بعد التسجيل.',
  featureValidation: 'التحقق من البيانات',
  featureValidationText: 'يتم التحقق من كلمة المرور ورمز الأمان قبل الإرسال.',
  panelTitle: 'إنشاء حساب لاعب',
  panelDescription: 'أدخل معلومات الحساب لإكمال التسجيل.',
  inviteCurrent: 'رمز الدعوة: ',
  inviteMissing: 'لا يحتوي هذا الرابط على رمز دعوة.',
  footer: 'لديك حساب بالفعل؟ العودة إلى تسجيل الدخول',
  username: 'اسم الحساب',
  usernameRequired: 'يرجى إدخال اسم الحساب.',
  usernameMin: 'يجب ألا يقل اسم الحساب عن 6 أحرف.',
  usernameMax: 'يجب ألا يزيد اسم الحساب عن 20 حرفًا.',
  usernamePlaceholder: 'استخدم 6–20 حرفًا أو رقمًا',
  password: 'كلمة المرور',
  passwordRequired: 'يرجى إدخال كلمة المرور.',
  passwordMin: 'يجب ألا تقل كلمة المرور عن 6 أحرف.',
  passwordPlaceholder: '6 أحرف على الأقل',
  confirmPassword: 'تأكيد كلمة المرور',
  confirmRequired: 'يرجى تأكيد كلمة المرور.',
  confirmMismatch: 'كلمتا المرور غير متطابقتين.',
  confirmPlaceholder: 'أدخل كلمة المرور مرة أخرى',
  captchaRequired: 'يرجى إدخال رمز الأمان.',
  captchaLength: 'يتكون رمز الأمان من 4 أحرف.',
  captchaPlaceholder: 'رمز من 4 أحرف',
  refreshCaptcha: 'تحديث رمز الأمان',
  submit: 'إنشاء الحساب',
  lightMode: 'الوضع الفاتح',
  darkMode: 'الوضع الداكن',
  language: 'اللغة'
};

const REGISTER_COPY = {
  'zh-CN': REGISTER_COPY_ZH,
  'en-US': REGISTER_COPY_EN,
  'ru-RU': REGISTER_COPY_RU,
  'vi-VN': REGISTER_COPY_VI,
  'ar-SA': REGISTER_COPY_AR
};

function Register({ themeMode, onToggleThemeMode, language = 'zh-CN', onLanguageChange }) {
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [captchaId, setCaptchaId] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const normalizedLanguage = normalizeUiLanguage(language);
  const copy = REGISTER_COPY[normalizedLanguage] || REGISTER_COPY_EN;

  const loadCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    try {
      const response = await publicAPI.getCaptcha();
      if (response.data.success) {
        setCaptchaId(response.data.data.captchaId);
        setCaptchaImage(response.data.data.image);
      }
    } catch (error) {
      console.error('Load captcha error:', error);
      message.error(copy.captchaLoadError);
    } finally {
      setCaptchaLoading(false);
    }
  }, [copy.captchaLoadError]);

  useEffect(() => {
    const code = searchParams.get('invite_code') || searchParams.get('inviteCode') || '';
    setInviteCode(code);
    loadCaptcha();
  }, [searchParams, loadCaptcha]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      await publicAPI.register({
        username: values.username,
        password: values.password,
        invite_code: inviteCode,
        captchaId,
        captchaCode: values.captcha,
        language: getGameLanguageId(language)
      });

      message.success(copy.success);
      form.resetFields(['password', 'confirmPassword', 'captcha']);
      loadCaptcha();
    } catch (error) {
      console.error('Register error:', error);
      message.error(normalizedLanguage === 'zh-CN' ? (error.response?.data?.message || copy.error) : copy.error);
      form.resetFields(['captcha']);
      loadCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const features = useMemo(() => [
    { icon: <LinkOutlined />, title: copy.featureInvite, text: copy.featureInviteText },
    { icon: <UserAddOutlined />, title: copy.featureAccount, text: copy.featureAccountText },
    { icon: <GiftOutlined />, title: copy.featureRelation, text: copy.featureRelationText },
    { icon: <CheckCircleOutlined />, title: copy.featureValidation, text: copy.featureValidationText }
  ], [copy]);

  return (
    <AuthShell
      variant="register"
      themeMode={themeMode}
      onToggleThemeMode={onToggleThemeMode}
      language={language}
      onLanguageChange={onLanguageChange}
      languageOptions={REGISTER_LANGUAGE_OPTIONS}
      lightModeLabel={copy.lightMode}
      darkModeLabel={copy.darkMode}
      languageLabel={copy.language}
      brandMark="R"
      brandName={copy.brandName}
      brandSubtitle={copy.brandSubtitle}
      eyebrow={copy.eyebrow}
      title={copy.title}
      description={copy.description}
      stats={[
        { label: copy.statType, value: copy.statRegister },
        { label: copy.statInvite, value: inviteCode ? copy.recognized : copy.missing },
        { label: copy.statCheck, value: copy.captcha }
      ]}
      featureTitle={copy.featureTitle}
      features={features}
      panelEyebrow="Create Account"
      panelTitle={copy.panelTitle}
      panelDescription={copy.panelDescription}
      panelNotice={inviteCode ? `${copy.inviteCurrent}${inviteCode}` : copy.inviteMissing}
      footer={(
        <Link className="auth-text-link" to="/login">
          <TeamOutlined />
          {copy.footer}
        </Link>
      )}
    >
      <Form
        form={form}
        name="register"
        layout="vertical"
        className="auth-form"
        onFinish={onFinish}
        autoComplete="off"
        size="large"
      >
        <Form.Item
          label={copy.username}
          name="username"
          rules={[
            { required: true, message: copy.usernameRequired },
            { min: 6, message: copy.usernameMin },
            { max: 20, message: copy.usernameMax }
          ]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder={copy.usernamePlaceholder}
            autoComplete="username"
            disabled={loading}
          />
        </Form.Item>

        <Form.Item
          label={copy.password}
          name="password"
          rules={[
            { required: true, message: copy.passwordRequired },
            { min: 6, message: copy.passwordMin }
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={copy.passwordPlaceholder}
            autoComplete="new-password"
            disabled={loading}
          />
        </Form.Item>

        <Form.Item
          label={copy.confirmPassword}
          name="confirmPassword"
          dependencies={['password']}
          rules={[
            { required: true, message: copy.confirmRequired },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(copy.confirmMismatch));
              }
            })
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={copy.confirmPlaceholder}
            autoComplete="new-password"
            disabled={loading}
          />
        </Form.Item>

        <Form.Item
          label={copy.captcha}
          name="captcha"
          rules={[
            { required: true, message: copy.captchaRequired },
            { len: 4, message: copy.captchaLength }
          ]}
        >
          <div className="auth-captcha-row">
            <Input
              prefix={<SafetyCertificateOutlined />}
              placeholder={copy.captchaPlaceholder}
              maxLength={4}
              disabled={loading}
            />
            <button
              className="auth-captcha-button"
              type="button"
              onClick={loadCaptcha}
              disabled={captchaLoading}
              aria-label={copy.refreshCaptcha}
              title={copy.refreshCaptcha}
            >
              {captchaLoading ? <ReloadOutlined spin /> : captchaImage ? <img src={captchaImage} alt={copy.captcha} /> : <ReloadOutlined />}
            </button>
          </div>
        </Form.Item>

        <Button
          className="auth-submit auth-submit-register"
          type="primary"
          htmlType="submit"
          loading={loading}
          icon={<SendOutlined />}
          block
        >
          {copy.submit}
        </Button>
      </Form>
    </AuthShell>
  );
}

export default Register;
